import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
    GraduationCap, Plus, Upload, Download, RefreshCw, Trash2, Users, BadgeCheck,
    ChevronLeft, Loader2, FileSpreadsheet, CheckCircle2, CircleDashed, X,
} from 'lucide-react';

const TEMPLATE_HEADERS = [
    'Depo Code', 'Zone', 'Depo', 'Division', 'Training Month', 'Training date',
    'Training Type', 'State', 'Training Location', 'Participant', 'Electrician Name', 'Contact Number',
];
const SAMPLE_ROW = [
    'DP001', 'North', 'Lucknow', 'Div-A', 'August', '2026-08-01', 'Classroom', 'UP',
    'Lucknow Centre', 'P1', 'Ramesh Kumar', '9800000001',
];

function downloadTemplate() {
    const csv = [TEMPLATE_HEADERS.join(','), SAMPLE_ROW.join(',')].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'offline-training-template.csv';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
}

export default function AdminOfflineTraining() {
    const [batches, setBatches] = useState(null);
    const [newName, setNewName] = useState('');
    const [newBadge, setNewBadge] = useState('');
    const [creating, setCreating] = useState(false);
    const [selected, setSelected] = useState(null); // {batch, learners}
    const [loadingDetail, setLoadingDetail] = useState(false);

    const load = useCallback(() => {
        api.get('/admin/offline/batches').then((r) => setBatches(r.data)).catch(() => setBatches([]));
    }, []);
    useEffect(() => { load(); }, [load]);

    const createBatch = async () => {
        if (!newName.trim()) return toast.error('Enter a batch name');
        setCreating(true);
        try {
            await api.post('/admin/offline/batches', { name: newName.trim(), badge_name: newBadge.trim() || undefined });
            toast.success('Batch created'); setNewName(''); setNewBadge(''); load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create batch'); }
        finally { setCreating(false); }
    };

    const openBatch = async (id) => {
        setLoadingDetail(true); setSelected({ batch: { id }, learners: [] });
        try {
            const r = await api.get(`/admin/offline/batches/${id}`);
            setSelected(r.data);
        } catch { toast.error('Could not open batch'); setSelected(null); }
        finally { setLoadingDetail(false); }
    };

    const deleteBatch = async (id, name) => {
        if (!window.confirm(`Delete batch "${name}"? Learners already certified keep their badge.`)) return;
        try { await api.delete(`/admin/offline/batches/${id}`); toast.success('Batch deleted'); setSelected(null); load(); }
        catch { toast.error('Delete failed'); }
    };

    if (selected) {
        return <BatchDetail data={selected} loading={loadingDetail} onBack={() => { setSelected(null); load(); }}
            onReload={() => openBatch(selected.batch.id)} onDelete={deleteBatch} />;
    }

    return (
        <div className="max-w-5xl mx-auto" data-testid="admin-offline-training">
            <div className="flex items-center gap-3 mb-1">
                <span className="grid place-items-center w-10 h-10 rounded-xl bg-brand-yellow/20 text-brand-navy"><GraduationCap className="h-5 w-5" /></span>
                <div>
                    <h1 className="font-display font-black text-2xl text-slate-900">Offline Training</h1>
                    <p className="text-sm text-slate-500">Certify learners from in-person classes. Create a batch, import the roster, and each matched learner earns the batch's certification badge.</p>
                </div>
            </div>

            {/* Create batch */}
            <div className="mt-5 bg-white rounded-2xl border border-slate-200 p-4" data-testid="offline-create-card">
                <div className="text-sm font-bold text-slate-800 mb-2">Create a new batch</div>
                <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2">
                    <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Batch name (e.g. Volt Master)"
                        className="px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="offline-batch-name" />
                    <input value={newBadge} onChange={(e) => setNewBadge(e.target.value)} placeholder="Badge name (optional)"
                        className="px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="offline-batch-badge" />
                    <button onClick={createBatch} disabled={creating}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-brand-navy text-white font-semibold text-sm hover:opacity-90 disabled:opacity-60" data-testid="offline-create-btn">
                        {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create batch
                    </button>
                </div>
                <button onClick={downloadTemplate} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-blue hover:underline" data-testid="offline-template-btn">
                    <Download className="h-3.5 w-3.5" /> Download CSV template
                </button>
            </div>

            {/* Batches list */}
            <div className="mt-5">
                {batches === null ? (
                    <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
                ) : batches.length === 0 ? (
                    <div className="text-center py-16 text-slate-400 text-sm" data-testid="offline-empty">No batches yet. Create one above to get started.</div>
                ) : (
                    <div className="grid sm:grid-cols-2 gap-3" data-testid="offline-batches">
                        {batches.map((b) => (
                            <div key={b.id} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`offline-batch-${b.id}`}>
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="font-display font-bold text-slate-900 truncate">{b.name}</div>
                                        <div className="text-[12px] text-slate-500 inline-flex items-center gap-1 mt-0.5"><BadgeCheck className="h-3.5 w-3.5 text-emerald-500" /> {b.badge_name}</div>
                                    </div>
                                    <button onClick={() => deleteBatch(b.id, b.name)} className="text-slate-300 hover:text-rose-500" data-testid={`offline-delete-${b.id}`}><Trash2 className="h-4 w-4" /></button>
                                </div>
                                <div className="flex items-center gap-4 mt-3 text-sm">
                                    <span className="inline-flex items-center gap-1 text-slate-700 font-semibold"><Users className="h-4 w-4 text-slate-400" /> {b.learner_count} learners</span>
                                    <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold"><CheckCircle2 className="h-4 w-4" /> {b.matched_count} matched</span>
                                </div>
                                <div className="flex items-center justify-between mt-3">
                                    <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-lg ${b.zeapl_status === 'synced' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                                        {b.zeapl_status === 'synced' ? <CheckCircle2 className="h-3 w-3" /> : <CircleDashed className="h-3 w-3" />} Zeapl: {b.zeapl_status}
                                    </span>
                                    <button onClick={() => openBatch(b.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 text-slate-800 font-semibold text-xs hover:bg-slate-200" data-testid={`offline-open-${b.id}`}>
                                        Open <ChevronLeft className="h-3.5 w-3.5 rotate-180" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function BatchDetail({ data, loading, onBack, onReload, onDelete }) {
    const batch = data.batch || {};
    const learners = data.learners || [];
    const [importing, setImporting] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [result, setResult] = useState(null);
    const fileRef = useRef(null);

    const onPickFile = async (e) => {
        const file = e.target.files?.[0];
        if (fileRef.current) fileRef.current.value = '';
        if (!file) return;
        setImporting(true); setResult(null);
        try {
            const fd = new FormData(); fd.append('file', file);
            const r = await api.post(`/admin/offline/batches/${batch.id}/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setResult(r.data);
            toast.success(`Imported: ${r.data.created} new, ${r.data.updated} updated, ${r.data.matched_this_import} matched`);
            onReload();
        } catch (err) { toast.error(err?.response?.data?.detail || 'Import failed'); }
        finally { setImporting(false); }
    };

    const doExport = async () => {
        try {
            const r = await api.get(`/admin/offline/batches/${batch.id}/export`, { responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url; a.download = `offline-${batch.slug || 'batch'}.csv`;
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        } catch { toast.error('Export failed'); }
    };

    const doSync = async () => {
        setSyncing(true);
        try {
            const r = await api.post(`/admin/offline/batches/${batch.id}/sync`);
            toast.success(`Synced ${r.data.synced_count} learners (recorded)`);
            onReload();
        } catch { toast.error('Sync failed'); }
        finally { setSyncing(false); }
    };

    return (
        <div className="max-w-5xl mx-auto" data-testid="offline-batch-detail">
            <button onClick={onBack} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900 mb-3" data-testid="offline-back-btn">
                <ChevronLeft className="h-4 w-4" /> All batches
            </button>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="font-display font-black text-2xl text-slate-900">{batch.name}</h1>
                    <div className="text-[13px] text-slate-500 inline-flex items-center gap-1.5 mt-1"><BadgeCheck className="h-4 w-4 text-emerald-500" /> Certification badge: <b className="text-slate-700">{batch.badge_name}</b></div>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => fileRef.current?.click()} disabled={importing}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-navy text-white font-semibold text-sm hover:opacity-90 disabled:opacity-60" data-testid="offline-import-btn">
                        {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import roster
                    </button>
                    <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onPickFile} className="hidden" data-testid="offline-file-input" />
                    <button onClick={doExport} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-100 text-slate-800 font-semibold text-sm hover:bg-slate-200" data-testid="offline-export-btn">
                        <Download className="h-4 w-4" /> Export
                    </button>
                    <button onClick={doSync} disabled={syncing}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-60" data-testid="offline-sync-btn">
                        {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync to Zeapl
                    </button>
                </div>
            </div>

            <div className="flex items-center gap-3 mt-3">
                <span className="inline-flex items-center gap-1 text-sm text-slate-700 font-semibold" data-testid="offline-detail-count"><Users className="h-4 w-4 text-slate-400" /> {batch.learner_count || learners.length} learners</span>
                <span className="inline-flex items-center gap-1 text-sm text-emerald-600 font-semibold"><CheckCircle2 className="h-4 w-4" /> {batch.matched_count || 0} matched to app accounts</span>
                <button onClick={() => onDelete(batch.id, batch.name)} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-rose-500 hover:text-rose-700"><Trash2 className="h-3.5 w-3.5" /> Delete batch</button>
            </div>

            {result && (
                <div className="mt-3 rounded-xl bg-blue-50 border border-blue-100 p-3 text-[13px] text-blue-800 flex items-center gap-2" data-testid="offline-import-result">
                    <FileSpreadsheet className="h-4 w-4" />
                    Imported {result.created} new + {result.updated} updated · {result.matched_this_import} matched to accounts · {result.skipped} skipped
                    {result.errors?.length ? ` · ${result.errors.length} errors` : ''}
                </div>
            )}

            {/* Learners table */}
            <div className="mt-4 bg-white rounded-2xl border border-slate-200 overflow-hidden">
                {loading ? (
                    <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
                ) : learners.length === 0 ? (
                    <div className="text-center py-16 text-slate-400 text-sm" data-testid="offline-learners-empty">
                        No learners yet. Click <b>Import roster</b> to upload a CSV/Excel of offline-certified learners.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="offline-learners-table">
                            <thead>
                                <tr className="text-slate-400 text-[11px] uppercase tracking-wide border-b border-slate-100">
                                    <th className="text-left py-2 px-3">Electrician</th>
                                    <th className="text-left py-2 px-3">Contact</th>
                                    <th className="text-left py-2 px-3">Depo</th>
                                    <th className="text-left py-2 px-3">State</th>
                                    <th className="text-left py-2 px-3">Training</th>
                                    <th className="text-left py-2 px-3">Account</th>
                                </tr>
                            </thead>
                            <tbody>
                                {learners.slice(0, 300).map((l) => (
                                    <tr key={l.id} className="border-b border-slate-50" data-testid={`offline-learner-${l.id}`}>
                                        <td className="py-2 px-3 font-semibold text-slate-800">{l.electrician_name || '—'}</td>
                                        <td className="py-2 px-3 text-slate-600">{l.contact_number || '—'}</td>
                                        <td className="py-2 px-3 text-slate-600">{l.depo || '—'}</td>
                                        <td className="py-2 px-3 text-slate-600">{l.state || '—'}</td>
                                        <td className="py-2 px-3 text-slate-600">{l.training_type || '—'} {l.training_date ? `· ${l.training_date}` : ''}</td>
                                        <td className="py-2 px-3">
                                            {l.matched
                                                ? <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> Matched</span>
                                                : <span className="inline-flex items-center gap-1 text-slate-400 font-semibold text-xs"><X className="h-3.5 w-3.5" /> No account</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {learners.length > 300 && <div className="text-center text-xs text-slate-400 py-2">Showing first 300 · use Export for the full list</div>}
                    </div>
                )}
            </div>
        </div>
    );
}
